import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, signal, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Router, UrlSerializer} from '@angular/router';
import {CURRENT_PLATFORM} from '../../../core/services/env.service';
import {Platform} from '../../../core/models/platform.enum';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {EscapeKeyService} from '../../../core/services/escape-key.service';
import {URL_PARAM_KEY_SEARCH_TERM, UrlService} from '../../../core/services/url.service';
import {
  Tile,
  TILE_CONDITIONS_QTY_SIMPLE_ANALOG,
  TILE_CONDITIONS_QTY_SIMPLE_BOOL,
  TILE_CONDITIONS_QTY_SIMPLE_MULTISTATE,
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TileFolderDisplayMode,
  TileId,
} from '../../../shared/models/tile';
import {
  SaveRevertButtonsComponent,
  SaveRevertButtonsStatus
} from '../save-revert-buttons/save-revert-buttons.component';
import {TilesService} from '../../../core/services/tiles.service';
import {
  CanvasStructure,
  Condition,
  ConditionMode,
  HasConditionsStructure,
  SimpleAnalogStructure,
  SimpleBoolStructure,
  SimpleMultistateStructure,
  SimpleStructure,
  SuperFolderStructure,
} from '../../../shared/models/structure';
import { Device, DeviceType } from '../../../shared/models/device';
import { DevicesService } from '../../../core/services/devices.service';
import { FORMULA_TEMPLATES } from '@features/formula-tile/shared/constants/formula-templates.constants';
import { FormulaType } from '../../../shared/models/formula-config';
import { FormulaService, FormulaSegment } from '@features/formula-tile/shared/services/formula.service';
import { FormulaVariablePillComponent } from '@features/formula-tile/creator/components/formula-variable-pill/formula-variable-pill.component';
import { PillColor } from '@features/formula-tile/creator/components/formula-variable-pill/formula-variable-pill.model';
import {Appearance, AppearanceConfig, ColorOption} from '../../../shared/models/appearance';
import {TileIcon} from '../../../shared/models/tile-icon';
import {BehaviorSubject, forkJoin, Observable, Subject, of} from 'rxjs';
import {App, AppId, APP_TYPE_PICTURE} from '../../../shared/models/app';
import {AppsService} from '../../../core/services/apps.service';
import {MetadataService, TileMetadata} from '../../../core/services/metadata.service';
import { TileValidationService } from '../../../core/services/tile-validation.service';
import {
  DECIMAL_OPTIONS,
  CONVERSION_OPTIONS,
  MULTIPLIER_OPTIONS,
  MAX_ALARM_DELAY,
  HIGH_ALARM_OPERATORS,
  LOW_ALARM_OPERATORS,
  CompareOperator
} from '@src/shared/constants/tile-config.constants';
import {SortTable} from '../../../shared/models/sort-table/types';
import {
  SortTableCellButtons,
  SortTableCellKebabMenu,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../shared/models/sort-table/cell-classes';
import {DisplayState} from '../../../shared/models/display-state';
import {BacnetPriorityArray} from '../../../shared/models/bacnet';
import {AutoClearOptions} from '../../../shared/models/alarm';
import { parseLoRaWANMetadata, parseModbusMetadata } from '../../../shared/utils/metadata-parser.util';
import {CdkOverlayOrigin, ConnectedPosition} from "@angular/cdk/overlay";
import {
  DimensionsHeight,
  TileDimensions
} from "../../../shared/models/tile-dimensions";
import {SpinnerComponent} from '../spinner/spinner.component';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {FormsModule} from '@angular/forms';
import {PillComponent} from '../pill/pill.component';
import {SortTableComponent} from '../sort-table/sort-table.component';
import {IconPickerComponent} from '../icon-picker/icon-picker.component';
import {PopoverIconPickerComponent} from '../popover-icon-picker/popover-icon-picker.component';
import { NgClass} from '@angular/common';
import {ColorPickerComponent} from '../color-picker/color-picker.component';
import {TileFolderContentsEditorComponent} from './folder-contents-editor/tile-folder-contents-editor.component';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {FolderViewFlipToggleComponent} from './folder-view-flip-toggle/folder-view-flip-toggle.component';
import {CanvasTileEditorComponent} from '@features/canvas-tiles/editor/components/canvas-tile-editor/canvas-tile-editor.component';
import { CanvasMiniTileComponent } from '@features/canvas-tiles/editor/components/canvas-mini-tile/canvas-mini-tile.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import { PillMultiselectComponent } from '../pill-multiselect/pill-multiselect.component';
import { PillOption, Role } from 'src/shared/models/fga/role';
import {Option, OptionGroup, OptionGroupMultiSelect} from '../../../shared/models/option';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import { take, catchError } from 'rxjs/operators';
import {UnsavedChangesService} from '@core/services/unsaved-changes.service';
import {TagService} from '../../../core/services/tag.service';
import {Facet, FacetValue, Tag, TagAssignment, TagType} from '../../../shared/models/tag';
import {ObjectType} from '../../../shared/models/object.model';
import {TagInputComponent} from '../tag-input/tag-input.component';
import {TAG_PROFILES} from '../../../shared/constants/tag-profiles.constants';
import { TilePollingService } from '../../../core/services/tile-polling.service';

interface AlarmConditionConfig {
  Title: string;
  Color: 'yellow' | 'red';
  Icon: 'fa-circle-chevron-down' | 'fa-circle-chevron-up' | 'fa-circle';
  Direction: '' | 'high' | 'low';
}


@Component({
  selector: 'motn-tile-editor',
  templateUrl: './tile-editor.component.html',
  styleUrls: ['./tile-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
    DrawerGapFromTopComponent,
    SaveRevertButtonsComponent,
    PillComponent,
    SortTableComponent,
    ColorPickerComponent,
    TileFolderContentsEditorComponent,
    PopoverMenuComponent,
    FolderViewFlipToggleComponent,
    CanvasTileEditorComponent,
    CanvasMiniTileComponent,
    IconPickerComponent,
    PopoverIconPickerComponent,
    NgClass,
    FormsModule,
    PillMultiselectComponent,
    FormulaVariablePillComponent,
    TagInputComponent,
    CdkOverlayOrigin,
    PopoverMultiSelectComponent,
  ],
})
export class TileEditorComponent implements AfterViewInit, OnDestroy {
  @Input() tileId: TileId;
  @Output() saved$: EventEmitter<null> = new EventEmitter<null>();
  @Output() deleted$: EventEmitter<TileId> = new EventEmitter<TileId>();
  @Output() closed$: EventEmitter<null> = new EventEmitter<null>();

  tile: Tile;
  private unregisterEsc = () => {};
  private unregisterUnsavedChanges: () => void;
  private isConfirmingClose = false;
  _tileConditionsStructure: HasConditionsStructure;
  _tileSimpleStructure: SimpleStructure;
  _tileSimpleBoolStructure: SimpleBoolStructure;
  _tileSimpleAnalogStructure: SimpleAnalogStructure;
  _tileSimpleMultistateStructure: SimpleMultistateStructure;
  _tileSuperFolderStructure: SuperFolderStructure;
  _tileCanvasStructure: CanvasStructure;
  _tileAppearance: Appearance;

  @ViewChild('saveRevertButtons') saveRevertButtons: SaveRevertButtonsComponent;

  // Signal for folder contents data (two-way binding with child)
  folderContentsData = signal<{
    orderedTileIds: TileId[]; // User's ordering (may include deleted tiles)
    orderedValidTileIds: TileId[]; // User's ordering filtered to only valid tiles
    addedTiles: TileId[];
    removedTiles: TileId[];
  } | null>(null);

  // Signal that increments on save to trigger deleted tiles cleanup in child
  cleanupDeletedTilesTrigger = signal<number>(0);

  conditions: Condition[] = [];
  conditionErrors: boolean[] = [];
  pickingIconCondition: null | number = null;

  minMaxError: boolean = false;

  stateTable$ = new BehaviorSubject<SortTable>(null);

  fetchingBacnetPriorityArray: boolean = false;
  bacnetPriorityArray: BacnetPriorityArray;
  bacnetPriorityArrayTable$ = new BehaviorSubject<SortTable>(null);

  displayPointId: string = '';
  tileMetadata: TileMetadata | null = null;
  scrollableHeightMenu = false;
  scrollableHeightPopoverPositions: ConnectedPosition[] = [{
    originX: 'center',
    offsetX: -10,
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  }];

  // Expose enum to template
  readonly DeviceType = DeviceType;

  appPillOptions: PillOption<Role>[] = [];
  appPillSelected: PillOption<Role>[] = [];
  originalAppPillSelected: PillOption<Role>[] = [];
  appPillOpenMenu$ = new Subject<void>();

  existingTileTags: Tag[] = [];
  pendingTileTags: Tag[] = [];
  pendingFacetValues: Record<string, FacetValue> = {};
  localFacetValues: Record<string, string[]> = {};
  selectedProfile: string | null = null;
  profileMenuOpen = false;
  profileOptions: OptionGroupMultiSelect | null = null;
  openFacetKey: string | null = null;
  readonly facetOverlayPositions: ConnectedPosition[] = [
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
    {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4},
  ];
  readonly ObjectType = ObjectType;

  get profileFacets(): Facet[] {
    return TAG_PROFILES[this.selectedProfile]?.Facets ?? [];
  }

  getOptionsForFacet(facetId: string): string[] {
    return [...this.tagService.getFacetValues(facetId), ...(this.localFacetValues[facetId] ?? [])];
  }

  get selectedProfileLabel(): string {
    return this.selectedProfile ? (TAG_PROFILES[this.selectedProfile]?.Label ?? this.selectedProfile) : '';
  }

  formulaDevice = signal<Device | null>(null);
  formulaDeviceLoading = signal(false);
  formulaPointValues = signal<Record<string, { value: number | boolean; time?: string } | null>>({});

  constructor(
    private shadeTools: ShadeToolsService,
    private appsService: AppsService,
    private tilesService: TilesService,
    private metadataService: MetadataService,
    private tileValidator: TileValidationService,
    private devicesService: DevicesService,
    private router: Router,
    private serializer: UrlSerializer,
    private escapeKeyService: EscapeKeyService,
    private tilePolling: TilePollingService,
    private formulaService: FormulaService,
    private tagService: TagService,
    private unsavedChanges: UnsavedChangesService,
  ) {
    for (let x = 0; x < 16; x++) {
      const val = x + 1;
      this.bacnetPriorityOptions.push(val);
    }

    this.unregisterUnsavedChanges = this.unsavedChanges.register(() => this.hasUnsavedCanvasChanges);
  }


  get hasUnsavedCanvasChanges(): boolean {
    return !!this.tile?.isCanvasTile() && this.saveRevertButtons?.status === 'edited';
  }


  ngAfterViewInit() {
    // TODO PLAT-266: Remove mock tile ID bypass when backend supports CANVAS tiles
    if (this.tileId === 'mock-canvas-tile-id') {
      // Skip permission check for mock Canvas tiles during development
      this.setUp();
      return;
    }

    this.tilesService.canConfigureTile$(this.tileId).subscribe({
      next: (canConfigure) => {
        if (canConfigure) {
          this.setUp();
        } else {
          this.shadeTools.unauthorizedDialog();
          this.close();
        }
      },
      error: () => {
        this.shadeTools.unauthorizedDialog();
        this.close();
      }
    });
  }


  private setUp() {
    forkJoin({
      apps: this.appsService.fetchUserApps$(),
      tile: this.tilesService.fetchTile$(this.tileId),
      metadata: this.fetchMetadataOptional(),
      linkedApps: this.appsService.getAppsLinkedToTile$(this.tileId).pipe(
        catchError(() => of([]))
      ),
    })
    .pipe(take(1))
    .subscribe({
      next: ({ apps, tile, metadata, linkedApps }) => {
        this.tile = Tile.create(tile);
        this.tileMetadata = metadata;
        this.setupTileData(this.tile);
        this.prepareAppOptions(apps, linkedApps);
        this.existingTileTags = this.tile.Tags ?? [];
        this.pendingTileTags = this.tile.tags;
        this.tagService.getUserTags$().pipe(take(1)).subscribe(() => {
          this.initFacetsFromTile(this.tile.facets);
        });
        this.unregisterEsc = this.escapeKeyService.register(() => this.close());

        const deviceId = (this.tile.Structure as SimpleStructure)?.DeviceID;

        if (this.tile.isFormulaTile()) {
          if (deviceId) {
            this.formulaDeviceLoading.set(true);
            this.devicesService.fetchDevice$(deviceId).pipe(take(1)).subscribe({
              next: device => {
                this.formulaDevice.set(device);
                this.formulaDeviceLoading.set(false);
                this.fetchFormulaPointValues(device);
                this.fetchFormulaSourceDevices(device);
              },
              error: () => { this.formulaDeviceLoading.set(false); },
            });
          }
        } else if (deviceId && !this.devicesService.sessUserDevices[deviceId]) {
          // Fetch device so its name appears in the Data Source section
          this.devicesService.fetchDevice$(deviceId).pipe(take(1)).subscribe();
        }
      },
      error: () => {
        this.shadeTools.errorDialog('Failed to load tile or apps.');
        this.close();
      }
    });
  }

  private fetchMetadataOptional() {
    return this.metadataService.getMetadata$(this.tileId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Assigns tile structure references and runs setup logic for the tile.
   */
  private setupTileData(tile: Tile): void {
    // Assign structure references for convenience
    this._tileConditionsStructure = tile.Structure as HasConditionsStructure;
    this._tileSimpleStructure = tile.Structure as SimpleStructure;
    this._tileSimpleBoolStructure = tile.Structure as SimpleBoolStructure;
    this._tileSimpleAnalogStructure = tile.Structure as SimpleAnalogStructure;
    this._tileSimpleMultistateStructure = tile.Structure as SimpleMultistateStructure;
    this._tileSuperFolderStructure = tile.Structure as SuperFolderStructure;
    this._tileCanvasStructure = tile.Structure as CanvasStructure;
    this._tileAppearance = tile.Appearance;

    // LoRaWAN tiles are always read-only
    if (this._tileSimpleStructure?.Protocol === DeviceType.LoRaWAN) {
      this._tileSimpleStructure.Writable = false;
    }

    this.prepareDisplayPointId();
    this.prepareConditions();

    switch (tile.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
        this.ensureAnalogDecimal();
        break;
      case TILE_TYPE_SIMPLE_MULTISTATE:
        this.renderStateTable();
        break;
    }
  }

  /**
   * Ensures analog decimal config is set to a default if not already set.
   */
  private ensureAnalogDecimal(): void {
    let decimal = this.tile.Appearance.Config.Decimal;
    if (!(decimal === 0) && !(decimal > 0)) {
      this.tile.Appearance.Config.Decimal = 2;
    }
  }


  validateMinMax() {
    const ac: AppearanceConfig = this.tile.Appearance.Config;

    if (!isNaN(Number(ac.MinAllowed))) {
      ac.MinAllowed = +ac.MinAllowed;
    } else {
      ac.MinAllowed = null;
    }

    if (!isNaN(Number(ac.MaxAllowed))) {
      ac.MaxAllowed = +ac.MaxAllowed;
    } else {
      ac.MaxAllowed = null;
    }

    // Use shared validation service
    const result = this.tileValidator.validateMinMax(ac.MinAllowed, ac.MaxAllowed);
    this.minMaxError = !result.valid;
  }


  delete() {
    this.shadeTools.startDialog(
      `Delete Tile ${this.tile.ID}`,
      `Are you sure you want to delete this tile?`,
      [
        {
          text: 'Yes, Delete Tile',
          callback: () => {
            this.tilesService.deleteTile$(this.tile.ID).subscribe(
              () => {
                this.deleted$.emit(this.tile.ID);
                this.shadeTools.closeAll();
              },
              () => {
                this.shadeTools.errorDialog();
              },
            );
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'No, Go Back',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      false,
      false,
      false,
      false,
    );
  }


  save() {
    // TODO PLAT-266: Remove mock save handling when backend supports CANVAS tiles
    if (this.tileId === 'mock-canvas-tile-id') {
      if (this.saveRevertButtons) {
        this.saveRevertButtons.showSavedStatus();
      }
      return;
    }

    for (let error of this.conditionErrors) {
      if (error) {
        this.shadeTools.errorDialog(
          'The numeric values specified for this tile\'s alarms conflict with one another.\n\n' +
          'The values must satisfy the following test: High Critical > High Warning > Low Warning > Low Critical\n\n' +
          'Values that fail the test are shown in red. Fix those values and then try saving again.',
        );

        this.updateSaveStatus('edited');
        return;
      }
    }

    const selectedAppIds: AppId[] = this.appPillSelected.map(p => p.key);
    const unselectedAppIds: AppId[] = this.appPillOptions.map(o => o.key).filter(key => !selectedAppIds.includes(key));

    // Handle folder tile children: update both TileIDs (order) and FGA relationships
    const saveOperations: Observable<unknown>[] = [
      this.tilesService.saveTile$(this.tile),
      this.tilesService.updateAppLinks$(this.tile.ID, selectedAppIds, unselectedAppIds),
    ];

    const allPendingAssignments: TagAssignment[] = [
      ...this.pendingTileTags.map(a => ({TagID: a.ID, Value: a.Value})),
      ...Object.values(this.pendingFacetValues).map(fv => ({TagID: fv.ID, Value: fv.Value})),
    ];
    const existingAssignments = this.existingTileTags.map(t => ({TagID: t.ID, Value: t.Value}));
    saveOperations.push(this.tagService.setObjectTags$(ObjectType.TILE, this.tile.ID, existingAssignments, allPendingAssignments));

    const folderData = this.folderContentsData();
    if (this.tile.typeSupportsHavingDescendants() && folderData) {
      // Update Structure.TileIDs (maintain user's ordering, exclude deleted tiles)
      const structure = this.tile.Structure as SuperFolderStructure;
      structure.TileIDs = folderData.orderedValidTileIds;

      // Clean up DataPoints array to remove deleted tiles
      const validTileIdsSet = new Set(folderData.orderedValidTileIds);
      if (structure.DataPoints?.length) {
        structure.DataPoints = structure.DataPoints.filter(point =>
          validTileIdsSet.has(point.TileID)
        );
      }

      // Update FGA relationships to match Structure.TileIDs (use PUT to replace entire set)
      // Only update FGA if children changed
      if (folderData.addedTiles.length > 0 || folderData.removedTiles.length > 0) {
        saveOperations.push(this.tilesService.replaceChildrenOfTile$(this.tile.ID, structure.TileIDs));
      }
    }

    // Handle canvas tile children: update FGA relationships to match canvas points
    if (this.tile.isCanvasTile()) {
      saveOperations.push(this.tilesService.replaceChildrenOfTile$(this.tile.ID, this.tile.getCanvasChildIds()));
    }

    forkJoin(saveOperations).subscribe(
      () => {
        if (this.saveRevertButtons) {
          this.saveRevertButtons.showSavedStatus();
        }
        const savedFacetTags = Object.values(this.pendingFacetValues);
        this.existingTileTags = [...this.pendingTileTags, ...savedFacetTags];
        this.tilesService.updateTileTags(this.tile.ID, this.existingTileTags);
        this.saved$.emit(null);
        // Trigger deleted tiles cleanup in folder contents editor
        this.cleanupDeletedTilesTrigger.update(v => v + 1);
        // Re-fetch tile values immediately so dashboard reflects config changes (e.g. transformations)
        this.tilePolling.refresh();
      },
      () => {
        this.shadeTools.errorDialog();
        this.updateSaveStatus('edited');
      },
      () => {
        if (this.saveRevertButtons) {
          this.saveRevertButtons.showSavedStatus();
        }
      },
    );
  }


  revert() {
    this.tile = null;
    this.setUp();
    this.appPillSelected = [...this.originalAppPillSelected];
    this.pendingTileTags = this.existingTileTags.filter(t => t.Type === TagType.TAG);
    this.pendingFacetValues = {};
    this.localFacetValues = {};
    this.selectedProfile = null;
    this.profileMenuOpen = false;
    this.profileOptions = null;
    this.openFacetKey = null;
    this.updateSaveStatus('');
  }


  close() {
    if (!this.hasUnsavedCanvasChanges) {
      this.closeEditor();
      return;
    }

    if (this.isConfirmingClose) {
      return;
    }

    this.isConfirmingClose = true;
    this.unregisterEsc();

    this.shadeTools.confirmDialog(
      'Unsaved Changes',
      'You have unsaved changes that will be lost if you close. Do you want to close without saving?',
      'Close',
      'Stay',
    ).then(confirmed => {
      this.isConfirmingClose = false;

      if (confirmed) {
        this.closeEditor();
      } else {
        this.unregisterEsc = this.escapeKeyService.register(() => this.close());
      }
    });
  }


  private closeEditor() {
    this.unregisterEsc();
    this.closed$.emit(null);
  }

  ngOnDestroy() {
    this.unregisterEsc();
    this.unregisterUnsavedChanges();
  }

  updateSaveStatus(status: SaveRevertButtonsStatus = 'edited') {
    if (this.saveRevertButtons) {
      this.saveRevertButtons.status = status;
    }
  }


  updateIcon(icon: TileIcon) {
    this.tile.Appearance.Icon = icon;
    this.updateSaveStatus();
  }

  updateColor(color: ColorOption) {
    this.tile.Appearance.Color = color;
    this.updateSaveStatus();
  }

  updateDisplayMode(displayMode: TileFolderDisplayMode) {
    this.structure.DisplayMode = displayMode
    this.tile.Appearance.Dimensions = new TileDimensions();
    this.updateSaveStatus();
  }

  onCanvasStructureChange(updatedStructure: CanvasStructure) {
    // Sync the canvas structure changes back to the tile
    this._tileCanvasStructure = updatedStructure;
    this.tile.Structure = updatedStructure;
    this.updateSaveStatus();
  }


  validateOffset() {
    if (typeof this._tileAppearance.Config.Offset !== 'number') {
      this._tileAppearance.Config.Offset = 0;
    }
  }


  addNewState() {
    this.shadeTools.startDialog(
      'Add State',
      'What is the raw numeric value the underlying device uses to define this state?\n\nThe value must only contain characters 0 through 9.',
      [
        {
          text: 'Save',
          callback: (input: string) => {
            if (/^\d+$/.test(input)) {
              const value = +input;

              if (this._tileSimpleMultistateStructure.DisplayStates.find(s => s.CompareFunc.Value === value)) {
                this.shadeTools.errorDialog('The value you entered already exists in the list of currently known states.');
                return;
              }

              const ds: DisplayState = {
                CompareFunc: {
                  Value: value,
                  Operator: CompareOperator.Equal,
                },
                DisplayText: input,
              };

              this._tileSimpleMultistateStructure.DisplayStates.push(ds);
              this.shadeTools.closeAll();
              this.renderStateTable();
              this.updateSaveStatus();
              return;
            }

            this.shadeTools.errorDialog('The value you entered did not follow the required format.\n\nEnsure you are entering digits only with no other kinds of characters.\n\nPlease try again.');
          },
        },
        {
          text: 'Cancel',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      true,
      false,
      false,
      false,
    );
  }


  fetchBacnetPriorityArray() {
    if (this.fetchingBacnetPriorityArray) {
      return;
    }

    this.fetchingBacnetPriorityArray = true;

    this.tilesService.fetchPriorityArrays$(this.tileId).subscribe(
      (data) => {
        this.bacnetPriorityArray = data[0];
        this.renderBacnetPriorityArrayTable();
        this.fetchingBacnetPriorityArray = false;
      },
      () => {
        this.shadeTools.errorDialog('A priority array could not be fetched. Make sure the point supports priority arrays and the hub is connected, and then try again.');
        this.fetchingBacnetPriorityArray = false;
      },
    );
  }


  renderBacnetPriorityArrayTable() {
    const table: SortTable = {
      css: {
        'width': '100%',
      },
      columns: [
        {
          name: 'Priority Number',
          css: {
            'width': '150px',
          },
        },
        {
          name: 'Current Value',
          css: {
            'width': 'calc(100% - 300px)',
          },
        },
        {
          name: '',
          css: {
            'width': '150px',
            'justify-content': 'right',
          },
        },
      ],
      rows: [],
    };

    const pointId = this.bacnetPriorityArray.PointID;
    const deviceId = this.bacnetPriorityArray.DeviceID;


    for (let priorityNumber in this.bacnetPriorityArray.Values) {
      const value = this.bacnetPriorityArray.Values[priorityNumber];
      const valuePill = new SortTableCellTextPill('' + value, '');

      table.rows.push({
        context: priorityNumber,
        cells: [
          new SortTableCellTitleDescription(priorityNumber, ''),
          valuePill,
          new SortTableCellButtons([
            {
              id: 'null',
              text: 'Clear This Value',
              colors: ['red'],
              callback: () => {
                this.shadeTools.startDialog(
                  'Clear Value',
                  `Are you sure you want to clear the value currently set at priority level ${priorityNumber}?`,
                  [
                    {
                      text: 'Yes, Clear It',
                      color: DialogBoxButtonColor.Red,
                      callback: () => {
                        const newPriArr: BacnetPriorityArray = {
                          PointID: pointId,
                          DeviceID: deviceId,
                          Values: {},
                        };

                        newPriArr.Values[priorityNumber] = null;

                        this.tilesService.sendPriorityArrays$([newPriArr]).subscribe(
                          (data) => {
                            const newVal = data[0].Values[priorityNumber];

                            valuePill.mainText = '' + newVal;
                            this.renderBacnetPriorityArrayTable();

                            if (newVal !== null) {
                              this.shadeTools.simpleOkDialog('Result', 'The command was sent successfully, but the device refused to clear the value.');
                            } else {
                              this.shadeTools.closeAll();
                            }
                          },
                          () => {
                            this.shadeTools.errorDialog();
                          },
                        );
                      },
                    },
                    {
                      text: 'No, Go Back',
                      color: DialogBoxButtonColor.Alt,
                      callback: () => {
                        this.shadeTools.closeAll();
                      },
                    },
                  ],
                  false,
                  false,
                  false,
                  false,
                );
              },
            },
          ]),
        ],
      });
    }

    this.bacnetPriorityArrayTable$.next(table);
  }


  private renderStateTable() {
    const table: SortTable = {
      css: {
        'width': '100%',
      },
      columns: [
        {
          name: 'Raw State Value',
          css: {
            'width': '180px',
          },
        },
        {
          name: 'Text Users See',
          css: {
            'width': 'calc(100% - 220px)',
          },
        },
        {
          name: '',
          css: {
            'width': '40px',
            'justify-content': 'right',
          },
        },
      ],
      rows: [],
    };

    for (let i in this._tileSimpleMultistateStructure.DisplayStates) {
      const displayState: DisplayState = this._tileSimpleMultistateStructure.DisplayStates[i];

      table.rows.push({
        context: '' + displayState.CompareFunc.Value + displayState.DisplayText,
        cells: [
          new SortTableCellTitleDescription('' + displayState.CompareFunc.Value, ''),
          new SortTableCellTextPill(displayState.DisplayText, ''),
          new SortTableCellKebabMenu(this.tile.ID, [
            {
              id: '0',
              name: 'Change text users see',
              callback: () => {
                this.shadeTools.startDialog(
                  'Edit',
                  'Define the text a user should see when the raw state value is: ' + displayState.CompareFunc.Value,
                  [
                    {
                      text: 'Save',
                      callback: (input: string) => {
                        displayState.DisplayText = input;
                        this.renderStateTable();
                        this.updateSaveStatus();
                        this.shadeTools.closeAll();
                      },
                    },
                    {
                      text: 'Cancel',
                      callback: () => {
                        this.shadeTools.closeAll();
                      },
                      color: DialogBoxButtonColor.Alt,
                    },
                  ],
                  true,
                );
              },
            },
            {
              id: '1',
              name: 'Delete this state',
              callback: () => {
                this.shadeTools.startDialog(
                  'Are You Sure?',
                  `After deleting this state, users will no longer see a human-friendly text description when the state is active.`,
                  [
                    {
                      text: 'Yes, Delete',
                      callback: () => {
                        this._tileSimpleMultistateStructure.DisplayStates.splice(+i, 1);
                        this.renderStateTable();
                        this.updateSaveStatus();
                        this.shadeTools.closeAll();
                      },
                      color: DialogBoxButtonColor.Red,
                    },
                    {
                      text: 'Cancel',
                      callback: () => {
                        this.shadeTools.closeAll();
                      },
                      color: DialogBoxButtonColor.Alt,
                    },
                  ],
                );
              },
            },
          ], '', false, 2),
        ],
      });
    }

    this.stateTable$.next(table);
  }


  /**
   * For BACnet multistate, this will show the user what the original state display text
   * strings were before the user may have created custom values
   */
  showOriginalStates(): void {
    let orig: string[] = [];

    for (let i = 0; i < this._tileSimpleMultistateStructure.BuiltInStates.length; i++) {
      orig.push(`${i + 1} – ${this._tileSimpleMultistateStructure.BuiltInStates[i]}`);
    }

    this.shadeTools.simpleOkDialog(
      'Original States',
      `These were the tile's original states, as pulled directly from the BACnet device:\n\n${orig.join(`\n`)}`,
    );
  }


  /**
   * Calculates the app option values from provided apps and linked apps
   */
  private prepareAppOptions(apps: App[], linkedApps: App[]): void {
    apps = apps.filter(app => app.Type === APP_TYPE_PICTURE);
    const linkedAppIds = new Set(linkedApps.map(app => app.ID));
    const pillOptions: PillOption<Role>[] = [];
    const pillSelected: PillOption<Role>[] = [];

    for (let app of apps) {
      const pill = { key: app.ID as Role, label: app.Name };
      pillOptions.push(pill);

      if (linkedAppIds.has(app.ID)) {
        pillSelected.push(pill);
      }
    }

    this.appPillOptions = pillOptions;
    this.appPillSelected = [...pillSelected];
    this.originalAppPillSelected = [...pillSelected];
  }

  prepareDisplayPointId(): void {
    const pointId = (this.tile.Structure as SimpleStructure)?.PointID;

    if (pointId) {
      try {
        const parts: string[] = JSON.parse(pointId);

        if (parts.length === 2) {
          this.displayPointId = `${parts[0]}:${parts[1]}`;
        }
      } catch (error) {
        // Handle invalid JSON gracefully - pointId may not be in expected format
        console.warn('Invalid PointID format:', pointId);
        this.displayPointId = '';
      }
    }
  }

  get deviceId(): string | undefined {
    return this._tileSimpleStructure?.DeviceID;
  }

  get deviceDisplayName(): string {
    if (!this.deviceId) return '—';
    return this.devicesService.sessUserDevices[this.deviceId]?.Name ?? this.deviceId;
  }

  get pointDisplayName(): string {
    if (this.tileMetadata?.Permanent?.discovery?.name) {
      return this.tileMetadata.Permanent.discovery.name;
    }
    const pointId = (this.tile?.Structure as SimpleStructure)?.PointID;
    if (!pointId) return '—';
    const device = this.deviceId ? this.devicesService.sessUserDevices[this.deviceId] : undefined;
    return this.pointIdToName(pointId, this._tileSimpleStructure?.Protocol, device);
  }

  get formulaIsCustom(): boolean {
    return this.formulaDevice()?.Config?.Formula?.Points?.[0]?.Formula?.Type === FormulaType.Custom;
  }

  get formulaDisplayName(): string {
    const point = this.formulaDevice()?.Config?.Formula?.Points?.[0];
    return FORMULA_TEMPLATES.find(t => t.id === point?.Formula?.Value)?.name ?? '—';
  }

  get formulaExpressionSegments(): FormulaSegment[] {
    const point = this.formulaDevice()?.Config?.Formula?.Points?.[0];
    if (point?.Formula?.Type !== FormulaType.Custom) return [];
    return this.formulaService.splitSegments(point.Formula.Value || '');
  }

  get formulaVariableInfos() {
    const device = this.formulaDevice();
    if (!device) return [];
    const point = device.Config?.Formula?.Points?.[0];
    if (!point) return [];
    const bindings = point.Bindings ?? [];
    const pointValues = this.formulaPointValues();

    const mapVariable = (key: string, label: string) => {
      const binding = bindings.find(b => b.Variable === key);
      const src = binding?.Source;
      const srcDevice = src ? this.devicesService.sessUserDevices[src.DeviceID] : undefined;
      const deviceName = src ? (srcDevice?.Name ?? src.DeviceID) : '—';
      const pointDisplay = src ? this.pointIdToName(src.PointID, srcDevice?.Type, srcDevice) : '—';
      const pointValue = pointValues[key] ?? null;
      return { key, label, deviceName, deviceId: src?.DeviceID, pointDisplay, pointValue, transformations: src?.Transformations };
    };

    if (point.Formula.Type === FormulaType.Template) {
      const template = FORMULA_TEMPLATES.find(t => t.id === point.Formula.Value) ?? null;
      if (!template) return [];
      return template.variables.map(v => mapVariable(v.key, v.label));
    }

    // Custom formula: derive variable list from expression (includes unbound variables)
    return this.formulaService.extractVariables(point.Formula.Value).map(key => mapVariable(key, key));
  }

  pillColorForVar(v: { deviceName: string }): PillColor {
    return v.deviceName !== '—' ? 'green' : 'red';
  }

  private fetchFormulaSourceDevices(device: Device): void {
    const bindings = device.Config?.Formula?.Points?.[0]?.Bindings ?? [];
    const uniqueDeviceIds = [...new Set(bindings.map(b => b.Source?.DeviceID).filter(Boolean))];
    uniqueDeviceIds.forEach(deviceId => {
      if (!this.devicesService.sessUserDevices[deviceId]) {
        this.devicesService.fetchDevice$(deviceId).pipe(take(1)).subscribe();
      }
    });
  }

  private fetchFormulaPointValues(device: Device): void {
    const bindings = device.Config?.Formula?.Points?.[0]?.Bindings ?? [];
    bindings.forEach(binding => {
      const { DeviceID, PointID } = binding.Source;
      this.devicesService.readPointValue$(PointID, DeviceID).pipe(take(1)).subscribe({
        next: values => {
          const pv = values?.[0];
          this.formulaPointValues.update(m => ({
            ...m,
            [binding.Variable]: pv != null ? { value: pv.Value, time: pv.Time } : null,
          }));
        },
        error: () => {},
      });
    });
  }

  private pointIdToName(pointId: string, protocol?: string, device?: Device): string {
    if (protocol === DeviceType.Modbus && device) {
      const match = device.Config?.Modbus?.Points?.find(p => p.ID === pointId);
      if (match?.Name) return match.Name;
    }
    try {
      const parts = JSON.parse(pointId);
      // LoRaWAN: [type, name, format] — name is parts[1]
      // Detect by protocol or by 3-element structure when protocol is unknown
      if (Array.isArray(parts) && parts[1] &&
          (protocol === DeviceType.LoRaWAN || (!protocol && parts.length === 3))) {
        return parts[1];
      }
    } catch {}
    return pointId || '—';
  }

  conversionLabel(value: string | undefined): string {
    if (!value) return 'None';
    return CONVERSION_OPTIONS.find(o => o.id === value)?.name ?? value;
  }

  multiplierLabel(value: number | undefined): string {
    if (value == null) return '—';
    return MULTIPLIER_OPTIONS.find(o => +o.id === value)?.name ?? String(value);
  }

  navigateToDevice(deviceId: string | undefined = this.deviceId): void {
    if (!deviceId) return;

    const queryParams = {[URL_PARAM_KEY_SEARCH_TERM]: JSON.stringify(deviceId)};

    if (CURRENT_PLATFORM === Platform.WEB) {
      const urlTree = this.router.createUrlTree([UrlService.controlsUrls.devicesExisting], {queryParams});
      window.open(window.location.origin + this.serializer.serialize(urlTree), '_blank');
    } else {
      this.closed$.emit(null);
      this.router.navigate([UrlService.controlsUrls.devicesExisting], {queryParams});
    }
  }

  get shouldShowMetadata(): boolean {
    const protocol = this._tileSimpleStructure?.Protocol;
    // LoRaWAN: always show, BACnet/Modbus: show if metadata exists or if protocol-specific data available
    return protocol === DeviceType.LoRaWAN ||
           protocol === DeviceType.Modbus ||
           !!(this.tileMetadata?.Permanent);
  }


  /**
   * SIMPLE_ANALOG:
   * Orders the tile's conditions into an array such that the array's order reflects
   * this... [high critical, high warning, low warning, low critical].
   *
   * SIMPLE_BOOLEAN and SIMPLE_MULTISTATE:
   * Orders the tile's conditions into an array reflecting... [critical, warning]
   *
   * Should only be called once during the course of configuring a single tile.
   */
  prepareConditions(): void {
    if (!this.tile.typeSupportsAlarms()) {
      return;
    }

    const structure = this._tileConditionsStructure;
    const conditions = [];

    switch (this.tile.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
        structure.Conditions.forEach((condition: Condition) => {
          if (condition.Mode === ConditionMode.Warning) {
            if (condition.Comparison.Operator.indexOf('<') > -1) {
              conditions[2] = {...condition};
            } else if (condition.Comparison.Operator.indexOf('>') > -1) {
              conditions[1] = {...condition};
            }
          } else if (condition.Mode === ConditionMode.Critical) {
            if (condition.Comparison.Operator.indexOf('<') > -1) {
              conditions[3] = {...condition};
            } else if (condition.Comparison.Operator.indexOf('>') > -1) {
              conditions[0] = {...condition};
            }
          }
        });

        this.alarmConfigs = this.analogAlarmConfigs;
        break;
      case TILE_TYPE_SIMPLE_BOOL:
        structure.Conditions.forEach((condition: Condition) => {
          if (condition.Mode === ConditionMode.Critical) {
            conditions[0] = condition;
          }
        });

        this.nonAnalogAlarmValueOptions = [
          {
            id: '0',
            name: this._tileSimpleBoolStructure.DisplayFalse,
          },
          {
            id: '1',
            name: this._tileSimpleBoolStructure.DisplayTrue,
          },
        ];

        this.alarmConfigs = this.nonAnalogAlarmConfigs;
        break;
      case TILE_TYPE_SIMPLE_MULTISTATE:
        structure.Conditions.forEach((condition: Condition) => {
          if (condition.Mode === ConditionMode.Critical) {
            conditions[0] = condition;
          }
        });

        this.nonAnalogAlarmValueOptions = this._tileSimpleMultistateStructure.DisplayStates.map(
          (ds): Option => {
            return {
              id: '' + ds.CompareFunc.Value,
              name: ds.DisplayText,
            };
          },
        );

        this.alarmConfigs = this.nonAnalogAlarmConfigs;
        break;
    }

    this.conditions = structure.Conditions = conditions;
    this.conditionErrors.length = this.conditionsLength;

    this.validateConditions();
  }


  /**
   * This function runs to ensure that alert condition threshold values make sense relative
   * to each other. For example, a critical high threshold value should always be greater
   * than a warning high.
   *
   * When bad numbers are found, we emit to this.conditionErrors[index] for all values that
   * are in conflict with at least one other value.
   */
  validateConditions(): void {
    switch (this.tile.Type) {
      case TILE_TYPE_SIMPLE_ANALOG:
        const s = this._tileSimpleAnalogStructure;
        const conditions: Condition[] = s.Conditions;

        // Parse values first (due to Angular bug: https://github.com/angular/angular/issues/13243)
        for (let index = 0; index < conditions.length; index++) {
          const condition = s.Conditions[index];
          if (condition.Enabled && typeof condition.Comparison.Value !== 'number') {
            condition.Comparison.Value = parseFloat('' + condition.Comparison.Value);
          }
        }

        // Use shared validation service for threshold values
        const result = this.tileValidator.validateThresholdValues(conditions);

        // Map validation errors to individual condition error flags
        // Reset all errors first
        this.conditionErrors = conditions.map(() => false);

        // If there are validation errors, mark all enabled conditions as errors
        // (The original logic wasn't very granular either)
        if (!result.valid) {
          for (let index = 0; index < conditions.length; index++) {
            if (conditions[index].Enabled) {
              this.conditionErrors[index] = true;
            }
          }
        }

        return;
      default:
        return;
    }
  }

  validateConditionsLive(index: number, rawValue: string): void {
    if (this.tile.Type !== TILE_TYPE_SIMPLE_ANALOG) {
      return;
    }

    const conditions: Condition[] = this._tileSimpleAnalogStructure.Conditions;
    const liveValue = parseFloat(rawValue);

    const previewConditions = conditions.map((condition, i) => i === index
      ? { ...condition, Comparison: { ...condition.Comparison, Value: liveValue } }
      : condition);

    const result = this.tileValidator.validateThresholdValues(previewConditions);

    this.conditionErrors = conditions.map(() => false);

    if (!result.valid) {
      for (let i = 0; i < conditions.length; i++) {
        if (conditions[i].Enabled) {
          this.conditionErrors[i] = true;
        }
      }
    }
  }

  validateDelays(): void {
    const s: HasConditionsStructure = <HasConditionsStructure>this.tile.Structure;
    const conditions: Condition[] = s.Conditions;

    for (let i = 0; i < conditions.length; i++) {
      const condition = s.Conditions[i];
      let delay = parseInt('' + condition.Delay);

      if (isNaN(delay)) {
        if ('' + condition.Delay !== '') {
          delay = 0;
        }
      } else {
        // Use shared validation service to check range
        const result = this.tileValidator.validateAlarmDelay(delay);
        if (!result.valid) {
          // Auto-correct to valid range
          if (delay < 0) {
            delay = 0;
          }
          if (delay > MAX_ALARM_DELAY) {
            delay = MAX_ALARM_DELAY;
          }
        }
      }

      condition.Delay = delay;
    }
  }


  warnOnConversionChange() {
    this.shadeTools.simpleOkDialog(
      'WARNING',
      'When changing the conversion formula, existing schedules will continue to run as programmed for the previously selected conversion formula.',
    );
  }


  /**
   *
   */
  get conditionsLength(): number {
    switch (this.tile.Type) {
      case TILE_TYPE_SIMPLE_BOOL:
        return TILE_CONDITIONS_QTY_SIMPLE_BOOL;
      case TILE_TYPE_SIMPLE_ANALOG:
        return TILE_CONDITIONS_QTY_SIMPLE_ANALOG;
      case TILE_TYPE_SIMPLE_MULTISTATE:
        return TILE_CONDITIONS_QTY_SIMPLE_MULTISTATE;
    }
  }


  alarmConfigs: AlarmConditionConfig[] = [];
  private nonAnalogAlarmConfigs: AlarmConditionConfig[] = [
    {
      Title: 'Critical',
      Color: 'red',
      Icon: 'fa-circle',
      Direction: '',
    },
  ];
  private analogAlarmConfigs: AlarmConditionConfig[] = [
    {
      Title: 'High Critical',
      Color: 'red',
      Icon: 'fa-circle-chevron-up',
      Direction: 'high',
    },
    {
      Title: 'High Warning',
      Color: 'yellow',
      Icon: 'fa-circle-chevron-up',
      Direction: 'high',
    },
    {
      Title: 'Low Warning',
      Color: 'yellow',
      Icon: 'fa-circle-chevron-down',
      Direction: 'low',
    },
    {
      Title: 'Low Critical',
      Color: 'red',
      Icon: 'fa-circle-chevron-down',
      Direction: 'low',
    },
  ];


  writabilityOptions: Option[] = [
    {
      id: 'false',
      name: "'Read-Only'",
    },
    {
      id: 'true',
      name: "'Read+Write'",
    }
  ]
  nonAnalogAlarmValueOptions: Option[] = [];
  bacnetPriorityOptions: number[] = [];
  incrementOptions: number[] = [10, 5, 1, 0.1, 0.5];
  decimalOptions = [...DECIMAL_OPTIONS];
  conversionOptions = CONVERSION_OPTIONS;
  multiplierOptions = MULTIPLIER_OPTIONS;
  highAlarmOperators = HIGH_ALARM_OPERATORS;
  lowAlarmOperators = LOW_ALARM_OPERATORS;

  readonly autoClearOptions = AutoClearOptions;

  get structure(): SuperFolderStructure {
    return this.tile.Structure as SuperFolderStructure;
  }

  get visibleDataPointsLength(): number {
    const folderData = this.folderContentsData();

    if (!folderData) {
      return this.structure?.TileIDs?.length || 0;
    }

    // Always use orderedValidTileIds (excludes deleted tiles)
    return folderData.orderedValidTileIds.length;
  }

  get isDataPointsViewMode(): boolean {
    return this.structure.DisplayMode === TileFolderDisplayMode.DataPoints
  };

  get folderDisplayMode(): TileFolderDisplayMode {
    // if we get empty display mode it's default
    return !!this.structure.DisplayMode ? this.structure.DisplayMode : TileFolderDisplayMode.Default;
  }

  showScrollableHeightMenu(): void {
    this.scrollableHeightMenu = true;
  }

  get scrollableHeightMenuOptions(): OptionGroup {
    const maxHeightQuantity = this.visibleDataPointsLength;
    const options = Array.from({ length: maxHeightQuantity }, (_, i) => ({
      id: `${i + 1}`,
      name: `${i + 1} ${this.tileHeight.Units}`
    }));
    return new OptionGroup(options);
  }


  get heightQuantity() {
    let heightQuantity: number;

    const visibleDataPointsQuantity = this.visibleDataPointsLength;
    if (visibleDataPointsQuantity && visibleDataPointsQuantity <= this.tileHeight.Quantity) {
      heightQuantity = visibleDataPointsQuantity;
    } else if (!visibleDataPointsQuantity) {
      heightQuantity = 0;
    } else {
      heightQuantity = this.tileHeight.Quantity;
    }
    return heightQuantity;
  }

  get tileHeight(): DimensionsHeight {
    return this.tile.Appearance?.Dimensions?.Height || new TileDimensions().Height
  }

  scrollableHeightMenuOptionSelected(heightOption: Option): void {
    this.scrollableHeightMenu = false;
    this.tile.Appearance.Dimensions = new TileDimensions(+heightOption.id);
    this.updateSaveStatus();
  }

  onTagsChanged(tags: Tag[]): void {
    this.pendingTileTags = tags;
    this.updateSaveStatus('edited');
  }

  private initFacetsFromTile(facetTags: FacetValue[]): void {
    if (!facetTags.length) return;

    const profileKey = Object.keys(TAG_PROFILES).find(key =>
      facetTags.some(t => TAG_PROFILES[key].Facets.some(f => f.ID === t.ID))
    );
    if (!profileKey) return;

    this.selectedProfile = profileKey;
    this.pendingFacetValues = Object.fromEntries(facetTags.map(t => [t.ID, t]));

    for (const t of facetTags) {
      this.tagService.addFacetValue(t.ID, t.Value);
    }
  }

  openProfileMenu(): void {
    const group = new OptionGroupMultiSelect();
    group.options = [
      {id: '', name: 'None', selected: this.selectedProfile === null},
      ...Object.keys(TAG_PROFILES).map(key => ({
        id: key,
        name: TAG_PROFILES[key].Label,
        selected: this.selectedProfile === key,
      })),
    ];
    this.profileOptions = group;
    this.profileMenuOpen = true;
  }

  closeProfileMenu(): void {
    this.profileMenuOpen = false;
    this.profileOptions = null;
  }

  toggleProfileMenu(): void {
    if (this.profileMenuOpen) {
      this.closeProfileMenu();
    } else {
      this.openProfileMenu();
    }
  }

  onProfileSelected(option: Option): void {
    this.selectedProfile = option.id || null;
    this.pendingFacetValues = {};
    this.localFacetValues = {};
    this.closeProfileMenu();
    this.updateSaveStatus('edited');
  }

  getFacetOptionGroup(values: string[], key: string): OptionGroupMultiSelect {
    const group = new OptionGroupMultiSelect();
    group.options = values.map(v => ({id: v, name: v, selected: this.pendingFacetValues[key]?.Value === v}));
    return group;
  }

  toggleFacet(key: string): void {
    this.openFacetKey = this.openFacetKey === key ? null : key;
  }

  clearFacet(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.onFacetValueChange(key, null);
  }

  onFacetOptionClicked(key: string, option: Option): void {
    const current = this.pendingFacetValues[key]?.Value ?? null;
    this.onFacetValueChange(key, current === option.id ? null : String(option.id));
    this.openFacetKey = null;
  }

  onFacetCreate(key: string, value: string): void {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return;
    this.localFacetValues = {...this.localFacetValues, [key]: [trimmed]};
    this.onFacetValueChange(key, trimmed);
    this.openFacetKey = null;
  }

  onFacetValueChange(key: string, value: string | null): void {
    if (value === null) {
      const copy = {...this.pendingFacetValues};
      delete copy[key];
      this.pendingFacetValues = copy;
    } else {
      const label = this.profileFacets.find(f => f.ID === key)?.Label ?? key;
      this.pendingFacetValues = {
        ...this.pendingFacetValues,
        [key]: {ID: key, Label: label, Type: TagType.FACET as const, Value: value},
      };
    }
    this.updateSaveStatus('edited');
  }

  onAppPillAdd(app: PillOption<Role>): void {
    if (!this.appPillSelected.some(a => a.key === app.key)) {
      this.appPillSelected = [...this.appPillSelected, app];
      this.updateSaveStatus('edited');
    }
  }

  onAppPillRemove(app: PillOption): void {
    this.appPillSelected = this.appPillSelected.filter(i => i.key !== app.key);
    this.updateSaveStatus('edited');
  }

  triggerAppPillMenu(): void {
    this.appPillOpenMenu$.next();
  }

  showMetadata(): void {
    const content = this.formatMetadataForDisplay();
    this.shadeTools.simpleOkDialog('Tile Metadata', content);
  }

  private formatMetadataForDisplay(): string {
    // Handle LoRaWAN point ID metadata
    if (this._tileSimpleStructure?.Protocol === DeviceType.LoRaWAN) {
      return this.formatLoRaWANMetadata();
    }

    // Handle Modbus point configuration metadata
    if (this._tileSimpleStructure?.Protocol === DeviceType.Modbus) {
      return this.formatModbusMetadata();
    }

    // Handle BACnet discovery metadata
    if (this._tileSimpleStructure?.Protocol === DeviceType.BACnet) {
      return this.formatBACnetMetadata();
    }

    // Fallback for other protocols or no metadata
    if (!this.tileMetadata?.Permanent) {
      return 'No metadata available for this tile.';
    }

    return JSON.stringify(this.tileMetadata.Permanent, null, 2);
  }

  private formatLoRaWANMetadata(): string {
    const pointId = this._tileSimpleStructure?.PointID;
    if (!pointId) {
      return 'No point ID available for this LoRaWAN tile.';
    }

    const parsed = parseLoRaWANMetadata(pointId);
    if (!parsed) {
      return `Error parsing LoRaWAN Point ID: ${pointId}`;
    }

    return parsed.displayText;
  }

  private formatModbusMetadata(): string {
    const pointId = this._tileSimpleStructure?.PointID;
    if (!pointId) {
      return 'No point ID available for this Modbus tile.';
    }

    const parsed = parseModbusMetadata(pointId);
    if (!parsed) {
      return `Error parsing Modbus Point ID: ${pointId}`;
    }

    return parsed.displayText;
  }

  private formatBACnetMetadata(): string {
    if (!this.tileMetadata?.Permanent) {
      return 'No metadata available for this tile.';
    }

    const { discovery } = this.tileMetadata.Permanent;
    if (!discovery) {
      return JSON.stringify(this.tileMetadata.Permanent, null, 2);
    }

    // Format BACnet discovery data in a user-friendly way
    const lines = [];

    if (discovery.name) {
      lines.push(`  Point Name: ${discovery.name}`);
    }

    lines.push(`  BACnet Object Type: ${discovery.type}`);
    lines.push(`  BACnet Instance Number: ${discovery.address}`);

    if (discovery.units) {
      lines.push(`  Units: ${discovery.units}`);
    }
    if (discovery.description) {
      lines.push(`  Description: ${discovery.description}`);
    }

    // Add any other permanent metadata
    const otherKeys = Object.keys(this.tileMetadata.Permanent)
      .filter(key => key !== 'discovery');

    if (otherKeys.length > 0) {
      lines.push('\nOther Metadata:');
      otherKeys.forEach(key => {
        lines.push(`  ${key}: ${JSON.stringify(this.tileMetadata.Permanent[key])}`);
      });
    }

    return lines.join('\n');
  }
}
